import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { UserModel } from '../_models/user.model';
import { AuthModel } from '../_models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { DeviceType, Role } from 'src/app/core/app.constants';
import { FirebaseMessagingService } from 'src/app/core/services/firebase-messaging.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // public fields
  currentUser$: Observable<UserModel>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserModel>;
  isLoadingSubject: BehaviorSubject<boolean>;


  get currentUserValue(): UserModel {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserModel) {
    this.currentUserSubject.next(user);
  }

  private fcmStorageKey = `${environment.appVersion}_fcm_token`;

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
    private firebaseMessaging: FirebaseMessagingService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserModel>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);
  }

  // public methods
  login(email: string, password: string): Observable<UserModel> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(email, password).pipe(
      map((auth: AuthModel) => {
        const result = this.setAuthFromLocalStorage(auth);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      map((user: UserModel) => {
        if (user) {
          // Register FCM token in background after successful login
          this.registerFCMToken();
        }
        return user;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  private registerFCMToken(): void {
    this.firebaseMessaging.requestPermissionAndGetToken().then(token => {
      if (!token) return;
      localStorage.setItem(this.fcmStorageKey, token);
      this.authHttpService.addDeviceToken(token, DeviceType.WEB).subscribe({
        error: (err) => console.error('Failed to register FCM token', err)
      });
    });
  }

  resetPasswordForUser(token: string, newPassword: string) {
    return this.authHttpService.resetPasswordForUser(token, newPassword);
  }

  logout() {
    const fcmToken = localStorage.getItem(this.fcmStorageKey) || null;
    // Notify backend to deregister token, then clean up locally
    this.authHttpService.logout(fcmToken).subscribe({
      complete: () => {
        if (fcmToken) {
          this.firebaseMessaging.deleteToken();
          localStorage.removeItem(this.fcmStorageKey);
        }
      },
      error: () => {
        // Always clean up locally even if backend call fails
        if (fcmToken) {
          this.firebaseMessaging.deleteToken();
          localStorage.removeItem(this.fcmStorageKey);
        }
      }
    });
    localStorage.removeItem(this.authLocalStorageToken);
    this.router.navigate(['/auth/login'], {
      queryParams: {
        returnUrl: this.router.url
      }
    });
  }

  isAdmin() {
    return (this.currentUserValue.role == Role.SUPER_ADMIN || this.currentUserValue.role == Role.ADMIN);
  }

  canAccess(access: string) {
    return (this.currentUserValue.role == Role.SUPER_ADMIN || this.currentUserValue.role == Role.RESTAURENT_ADMIN ||
      this.currentUserValue.access.includes(access));
  }

  isModuleAdmin() {
    return (this.currentUserValue.role == Role.ADMIN);
  }

  isRestaurant() {
    return (this.currentUserValue.role == Role.RESTAURENT_ADMIN);
  }

  profileImage() {

    if (!this.currentUserValue?.restaurant?.image) {
      return `url(./assets/media/users/blank.png)`;
    }
    return `url('${this.currentUserValue.restaurant.image}')`;

  }

  getUserByToken(): Observable<UserModel> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.token) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.token).pipe(
      map((user: UserModel) => {
        if (user) {
          this.currentUserSubject = new BehaviorSubject<UserModel>(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  addDeviceToken(token: string): Observable<void> {

    this.isLoadingSubject.next(true);
    return this.authHttpService.addDeviceToken(token, DeviceType.WEB).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // need create new user then login
  registration(user: UserModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(email: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.authHttpService.changePassword(currentPassword, newPassword);
  }

  resetPassword(token: string, newPassword: string) {
    return this.authHttpService.resetPassword(token, newPassword);
  }

  updateProfile(model: UserModel) {
    return this.authHttpService.updateProfile(model);
  }

  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth accessToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.token) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  getAccessToken() {
    let auth = this.getAuthFromLocalStorage();
    return auth ? auth.token : null;
  }

  private getAuthFromLocalStorage(): AuthModel {
    try {
      const authData = JSON.parse(
        localStorage.getItem(this.authLocalStorageToken)
      );
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
